/**
 * Single-line diagram generator (RGIE style): supply chain on the left, a horizontal bus,
 * one column per circuit — protective device, circuit line, points branched to the right
 * and numbered with the circuit letter (A1, A2…). Used by "add circuit", the AI import
 * and the example project.
 */
import {
  applyTransaction,
  newId,
  nextCircuitCode,
  type Circuit,
  type Element,
  type Id,
  type Op,
  type Project,
  type PropValue,
  type Rotation,
  type Wire,
  type WireEnd,
} from '@elec/domain';
import { analyse, type Analysis } from '@elec/analysis';
import { pageLayout, paperSize } from '@elec/layout';
import { elementBounds, getSymbol, portPosition } from '@elec/symbols';

export interface PointSpec {
  symbolId: string;
  props?: Record<string, PropValue>;
}

export interface CircuitSpec {
  label: string;
  device: { symbolId: 'iec.breaker' | 'iec.rcbo' | 'iec.fuse'; props?: Record<string, PropValue> };
  points: PointSpec[];
  cable?: { type: string; sectionMm2: number; cores: number; lengthM?: number; installMethod?: string };
  legacy?: boolean;
}

export interface GroupSpec {
  /** RCD protecting the group; omitted for circuits fed directly from the main bus. */
  rcd?: Record<string, PropValue>;
  circuits: CircuitSpec[];
}

export interface BoardSpec {
  supply?: Record<string, PropValue>;
  meter?: boolean;
  main?: Record<string, PropValue>;
  /** Main RCD in the supply chain (typically 300 mA in Belgian dwellings). */
  mainRcd?: Record<string, PropValue>;
  groups: GroupSpec[];
  /**
   * Draw the earth electrode beside the supply (when the project has none yet), with its
   * measured resistance when known. Callers set it where the standard requires an electrode.
   */
  earth?: { resistanceOhm?: number };
}

export const COLUMN = 45; // mm between circuit columns
export const EARTH_ELECTRODE = 'iec.earth-electrode';
/** Where the earth electrode goes: left of the supply chain, as the survey draws it. */
const EARTH_SPOT = { x: 12, y: 70 };

/**
 * Draw the earth electrode (left of the supply chain, in the first free spot) or, when the
 * project has one, set its measured resistance. `resistanceOhm`: undefined leaves the value as
 * it is, null clears it. Returns no ops when there is nothing to change.
 */
export function earthElectrodeOps(
  project: Project,
  sheetId: Id | undefined,
  resistanceOhm?: number | null,
): { ops: Op[]; id?: Id; added: boolean } {
  const existing = Object.values(project.elements).find((e) => e.symbolId === EARTH_ELECTRODE);
  if (existing) {
    if (resistanceOhm === undefined || existing.props.resistanceOhm === (resistanceOhm ?? ''))
      return { ops: [], id: existing.id, added: false };
    return {
      ops: [
        {
          op: 'update',
          coll: 'elements',
          id: existing.id,
          patch: { props: { ...existing.props, resistanceOhm: resistanceOhm ?? '' } },
        },
      ],
      id: existing.id,
      added: false,
    };
  }
  if (!sheetId || !project.sheets[sheetId]) return { ops: [], added: false };
  const def = getSymbol(EARTH_ELECTRODE)!;
  const boxes = Object.values(project.elements)
    .filter((e) => e.sheetId === sheetId)
    .map((e) => {
      const d = getSymbol(e.symbolId);
      return d ? elementBounds(d, e) : null;
    })
    .filter((x): x is NonNullable<typeof x> => !!x);
  const clear = (x: number, y: number) =>
    boxes.every(
      (b) => x + def.width + 2 < b.minX || x - 2 > b.maxX || y + def.height + 2 < b.minY || y - 2 > b.maxY,
    );
  const spot =
    [0, 30, 60, 90, 120, -30]
      .map((dy) => ({ x: EARTH_SPOT.x, y: EARTH_SPOT.y + dy }))
      .find((c) => clear(c.x, c.y)) ?? EARTH_SPOT;
  const used = new Set(Object.values(project.elements).map((e) => e.tag));
  const id = newId();
  return {
    ops: [
      {
        op: 'add',
        coll: 'elements',
        value: {
          id,
          sheetId,
          symbolId: EARTH_ELECTRODE,
          ...spot,
          rotation: 0,
          mirrored: false,
          tag: used.has('PE') ? 'PE1' : 'PE',
          props: resistanceOhm != null ? { resistanceOhm } : {},
        },
      },
    ],
    id,
    added: true,
  };
}
export const POINT_STEP = 12.5;

export const cableLabel = (c: CircuitSpec['cable']) =>
  c ? `${c.type} ${c.cores}G${String(c.sectionMm2).replace('.', ',')}`.trim() : undefined;

class Builder {
  ops: Op[] = [];
  project: Project;
  /** Sheet being drawn on (changes when a board continues on another sheet). */
  sheetId: Id;
  constructor(project: Project, sheetId: Id) {
    this.project = project;
    this.sheetId = sheetId;
  }
  push(op: Op) {
    this.ops.push(op);
    this.project = applyTransaction(this.project, { label: '', ops: [op] }).project;
  }
  element(
    symbolId: string,
    x: number,
    y: number,
    tag: string,
    props: Record<string, PropValue> = {},
    rotation: Rotation = 0,
    circuitId?: Id,
  ): Id {
    const def = getSymbol(symbolId);
    const defaults: Record<string, PropValue> = {};
    for (const f of def?.fields ?? [])
      if ('default' in f && f.default !== undefined) defaults[f.key] = f.default;
    const id = newId();
    const el: Element = {
      id,
      sheetId: this.sheetId,
      symbolId,
      x,
      y,
      rotation,
      mirrored: false,
      tag,
      props: { ...defaults, ...props },
    };
    if (circuitId) el.circuitId = circuitId;
    this.push({ op: 'add', coll: 'elements', value: el });
    return id;
  }
  wire(from: WireEnd, to: WireEnd, label?: string) {
    this.push({
      op: 'add',
      coll: 'wires',
      value: { id: newId(), sheetId: this.sheetId, from, to, via: [], ...(label ? { label } : {}) },
    });
  }
  nextDeviceTag() {
    const used = new Set(Object.values(this.project.elements).map((e) => e.tag));
    for (let i = 1; ; i++) if (!used.has(`Q${i}`)) return `Q${i}`;
  }
}

const port = (elementId: Id, portId: string): WireEnd => ({ kind: 'port', elementId, portId });
const pt = (x: number, y: number): WireEnd => ({ kind: 'point', x, y });

/** Place one circuit column whose conductor is at x, fed from the bus at busY. */
function placeCircuit(b: Builder, spec: CircuitSpec, x: number, busY: number, fromBus: WireEnd | null): Id {
  const circuitId = newId();
  const code = nextCircuitCode(b.project);
  const circuit: Circuit = {
    id: circuitId,
    code,
    label: spec.label,
    cable: spec.cable ? { ...spec.cable } : { type: '', sectionMm2: 0, cores: 3 },
    legacy: !!spec.legacy,
  };
  b.push({ op: 'add', coll: 'circuits', value: circuit });
  const top = busY + 5;
  const dev = b.element(
    spec.device.symbolId,
    x - 5,
    top,
    b.nextDeviceTag(),
    spec.device.props ?? {},
    0,
    circuitId,
  );
  if (fromBus) b.wire(fromBus, port(dev, 'in'));
  const firstY = top + 30;
  const pts = spec.points.length ? spec.points : [];
  const lastY = firstY + Math.max(pts.length - 1, 0) * POINT_STEP;
  b.wire(port(dev, 'out'), pt(x, pts.length ? lastY : top + 27.5), cableLabel(spec.cable));
  pts.forEach((p, i) => {
    const y = firstY + i * POINT_STEP;
    const def = getSymbol(p.symbolId);
    if (!def || !def.ports[0]) return;
    // Rotated 270°: the point's terminal faces left, towards the circuit line.
    const tp = portPosition(def, { x: 0, y: 0, rotation: 270, mirrored: false }, def.ports[0]);
    const el = b.element(p.symbolId, x + 10 - tp.x, y - tp.y, `${code}${i + 1}`, p.props ?? {}, 270);
    b.wire(pt(x, y), port(el, def.ports[0].id));
  });
  return dev;
}

/**
 * Generate a whole board on an (empty) single-line sheet. A board wider than an A3 sheet
 * continues on further sheets: the main bus (and an RCD group's bus cut by the page end) ends
 * on an off-page connector and resumes on the next sheet from the connector with the same link.
 */
export function generateBoardOps(
  project: Project,
  sheetId: Id,
  board: BoardSpec,
  origin = { x: 35, y: 25 },
): Op[] {
  const b = new Builder(project, sheetId);
  const sheet = project.sheets[sheetId]!;
  const x0 = origin.x;
  // Pick A3 when the circuits do not fit across an A4 sheet.
  const columns = board.groups.reduce((n, g) => n + g.circuits.length, 0);
  const lastX = (paper: 'A4' | 'A3') => paperSize({ ...sheet, paper }).w - 10 - 40;
  const fits = (paper: 'A4' | 'A3') => x0 + (columns - 1) * COLUMN <= lastX(paper);
  // …or when the longest circuit would run into the A4 title block.
  const chainH =
    15 + (board.meter !== false ? 25 : 0) + (board.main ? 25 : 0) + (board.mainRcd ? 25 : 0) + 2.5;
  const deepest = Math.max(
    0,
    ...board.groups.flatMap((g) =>
      g.circuits.map(
        (c) =>
          origin.y + chainH + (g.rcd ? 32.5 : 0) + 35 + Math.max(c.points.length - 1, 0) * POINT_STEP + 10,
      ),
    ),
  );
  const tallFits = (paper: 'A4' | 'A3') =>
    deepest <= pageLayout(project, { ...sheet, paper }).titleBlock.y - 5;
  let paper = sheet.paper;
  if (sheet.paper === 'A4' && (!fits('A4') || !tallFits('A4'))) {
    paper = 'A3';
    b.push({ op: 'update', coll: 'sheets', id: sheetId, patch: { paper: 'A3' } });
  }
  let y = origin.y;
  let prev: WireEnd | null = null;
  const chain = (symbolId: string, tag: string, props: Record<string, PropValue>, height: number) => {
    const id = b.element(symbolId, x0 - 5, y, tag, props);
    if (prev) b.wire(prev, port(id, 'in'));
    prev = port(id, 'out');
    y += height + 5;
  };
  chain('iec.supply', 'W1', board.supply ?? {}, 10);
  if (board.meter !== false) chain('iec.meter', 'P1', {}, 20);
  if (board.main) chain('iec.switch-disconnector', b.nextDeviceTag(), board.main, 20);
  if (board.mainRcd) chain('iec.rcd', b.nextDeviceTag(), board.mainRcd, 20);
  const busY = y + 2.5;
  const subY = busY + 32.5;
  if (prev) b.wire(prev, pt(x0, busY));
  if (board.earth && !Object.values(project.elements).some((e) => e.symbolId === EARTH_ELECTRODE))
    b.element(
      EARTH_ELECTRODE,
      EARTH_SPOT.x,
      origin.y + 45,
      'PE',
      board.earth.resistanceOhm !== undefined ? { resistanceOhm: board.earth.resistanceOhm } : {},
    );

  // Page state: where the buses start on this sheet and the last column on them.
  let pageX0 = x0;
  let col = 0;
  let page = 1;
  let links = 0;
  let main = { start: x0, last: x0 };
  let sub: { start: WireEnd; startX: number; last: number } | null = null;
  const connector = (x: number, yy: number, link: string, incoming: boolean): WireEnd => {
    // Outgoing: port on the left at x. Incoming (mirrored): port on the right at x.
    const id = b.element('iec.offpage', incoming ? x - 10 : x, yy - 2.5, '', { link });
    if (incoming) b.push({ op: 'update', coll: 'elements', id, patch: { mirrored: true } });
    return port(id, 'a');
  };
  // Does anything after the current group still hang from the main bus?
  let mainNeededLater = true;
  const breakPage = () => {
    const end = (main.last > pageX0 || sub ? main.last : pageX0) + 20;
    const continueMain = !sub || mainNeededLater;
    const mainLink = continueMain ? `R${++links}` : '';
    if (continueMain) b.wire(pt(main.start, busY), connector(end, busY, mainLink, false));
    else if (main.last > main.start) b.wire(pt(main.start, busY), pt(main.last, busY));
    let subLink: string | undefined;
    if (sub) {
      subLink = `R${++links}`;
      b.wire(sub.start, connector(sub.last + 20, subY, subLink, false));
    }
    page++;
    const id = newId();
    b.push({
      op: 'add',
      coll: 'sheets',
      value: {
        id,
        name: `${sheet.name} (${page})`,
        kind: 'single-line',
        paper,
        orientation: sheet.orientation,
        order: sheet.order + (page - 1) / 100,
      },
    });
    b.sheetId = id;
    pageX0 = 35;
    col = 0;
    if (continueMain) {
      const inMain = connector(20, busY, mainLink, true);
      b.wire(inMain, pt(pageX0, busY));
    }
    main = { start: pageX0, last: pageX0 - COLUMN };
    if (sub && subLink) {
      const inSub = connector(20, subY, subLink, true);
      sub = { start: inSub, startX: 20, last: 20 };
    }
  };
  const nextX = () => {
    let x = pageX0 + col * COLUMN;
    if (x > lastX(paper)) {
      breakPage();
      x = pageX0;
    }
    col++;
    return x;
  };

  board.groups.forEach((g, gi) => {
    mainNeededLater = board.groups.slice(gi + 1).some((x) => x.circuits.length > 0);
    if (!g.rcd) {
      for (const c of g.circuits) {
        const x = nextX();
        main.last = x;
        placeCircuit(b, c, x, busY, pt(x, busY));
      }
      return;
    }
    const x = nextX();
    main.last = x;
    const rcd = b.element('iec.rcd', x - 5, busY + 5, b.nextDeviceTag(), g.rcd);
    b.wire(pt(x, busY), port(rcd, 'in'));
    b.wire(port(rcd, 'out'), pt(x, subY));
    sub = { start: pt(x, subY), startX: x, last: x };
    g.circuits.forEach((c, i) => {
      const cx = i === 0 ? x : nextX();
      sub!.last = cx;
      placeCircuit(b, c, cx, subY, pt(cx, subY));
    });
    if (sub.last > sub.startX) b.wire(sub.start, pt(sub.last, subY));
    sub = null;
  });
  if (main.last > main.start) b.wire(pt(main.start, busY), pt(main.last, busY));
  return b.ops;
}

/**
 * Shift everything right of `afterX` on a sheet by `dx` (elements by their centre, free wire
 * points and corners): makes room for a new column without breaking any connection.
 */
export function shiftRightOps(project: Project, sheetId: Id, afterX: number, dx: number): Op[] {
  const ops: Op[] = [];
  for (const e of Object.values(project.elements)) {
    if (e.sheetId !== sheetId) continue;
    const def = getSymbol(e.symbolId);
    const cx = e.x + (def ? def.width / 2 : 0);
    if (cx > afterX) ops.push({ op: 'update', coll: 'elements', id: e.id, patch: { x: e.x + dx } });
  }
  const mv = (end: WireEnd): WireEnd =>
    end.kind === 'point' && end.x > afterX ? { ...end, x: end.x + dx } : end;
  for (const w of Object.values(project.wires)) {
    if (w.sheetId !== sheetId) continue;
    const from = mv(w.from);
    const to = mv(w.to);
    const via = w.via.map((p) => (p.x > afterX ? { ...p, x: p.x + dx } : p));
    if (from !== w.from || to !== w.to || via.some((p, i) => p !== w.via[i]))
      ops.push({ op: 'update', coll: 'wires', id: w.id, patch: { from, to, via } });
  }
  return ops;
}

/**
 * Add one circuit after the circuits already fed by `rcdId` (or by the main bus when the
 * sheet has no RCD). Columns to the right move over to make room, and that group's bus is
 * extended — other groups' buses are never touched.
 */
export function appendCircuitOps(
  project: Project,
  sheetId: Id,
  spec: CircuitSpec,
  rcdId?: Id,
  analysis: Analysis = analyse(project),
): Op[] {
  const onSheet = analysis.circuits.filter((c) => c.sheet.id === sheetId);
  const siblings = onSheet.filter((c) => (rcdId ? c.rcds[0]?.id === rcdId : true));
  const conductor = (e: Element) => e.x + 5;
  if (siblings.length) {
    const right = siblings.reduce((a, c) => (conductor(c.device) > conductor(a.device) ? c : a));
    const rightX = conductor(right.device);
    const busY = right.device.y - 5;
    const shift = shiftRightOps(project, sheetId, rightX + 30, COLUMN);
    const shifted = applyTransaction(project, { label: '', ops: shift }).project;
    const b = new Builder(shifted, sheetId);
    const x = rightX + COLUMN;
    b.wire(pt(rightX, busY), pt(x, busY));
    placeCircuit(b, spec, x, busY, pt(x, busY));
    return [...shift, ...b.ops];
  }
  const b = new Builder(project, sheetId);
  const rcd = rcdId ? project.elements[rcdId] : undefined;
  if (rcd) {
    const x = rcd.x + 5;
    const busY = rcd.y + 20 + 7.5;
    b.wire(port(rcd.id, 'out'), pt(x, busY));
    placeCircuit(b, spec, x, busY, pt(x, busY));
    return b.ops;
  }
  // Empty sheet: a free-standing circuit near the top left.
  placeCircuit(b, spec, 40, 25, null);
  return b.ops;
}

/**
 * Add points (sockets, lights, appliances) at the bottom of an existing circuit: the circuit
 * line is extended downwards and each point is drawn as `placeCircuit` draws them, numbered
 * after the circuit's last point. Returns no ops when the circuit cannot be found.
 */
export function appendPointsOps(
  project: Project,
  deviceId: Id,
  points: PointSpec[],
  analysis: Analysis = analyse(project),
): Op[] {
  const c = analysis.circuits.find((x) => x.device.id === deviceId);
  const dev = project.elements[deviceId];
  const devDef = dev && getSymbol(dev.symbolId);
  const out = devDef?.ports.find((p) => p.id === 'out');
  if (!c || !dev || !devDef || !out || !points.length) return [];
  const b = new Builder(project, dev.sheetId);
  const x = portPosition(devDef, dev, out).x;
  // Where the circuit line ends: the terminal of its lowest point, or just below the device.
  const ends = c.points.flatMap((p) => {
    const d = getSymbol(p.element.symbolId);
    return d?.ports[0] ? [portPosition(d, p.element, d.ports[0]).y] : [];
  });
  let lastY = ends.length ? Math.max(...ends) : portPosition(devDef, dev, out).y + 2.5 - POINT_STEP;
  if (!ends.length) b.wire(port(dev.id, 'out'), pt(x, lastY + POINT_STEP));
  const prefix = c.code || dev.tag;
  // A new point without props takes those of the circuit's last point of the same kind (IP rating, room).
  const like = (symbolId: string) =>
    [...c.points].reverse().find((q) => q.element.symbolId === symbolId)?.element.props;
  points.forEach((p, i) => {
    const def = getSymbol(p.symbolId);
    if (!def || !def.ports[0]) return;
    const y = lastY + POINT_STEP;
    if (ends.length || i > 0) b.wire(pt(x, lastY), pt(x, y));
    const tp = portPosition(def, { x: 0, y: 0, rotation: 270, mirrored: false }, def.ports[0]);
    const el = b.element(
      p.symbolId,
      x + 10 - tp.x,
      y - tp.y,
      `${prefix}${c.points.length + i + 1}`,
      p.props ?? { ...(like(p.symbolId) ?? {}) },
      270,
    );
    b.wire(pt(x, y), port(el, def.ports[0].id));
    lastY = y;
  });
  return b.ops;
}

/**
 * Add a new RCD with its circuits at the right end of the board, fed from the main bus
 * (e.g. a dedicated 30 mA RCD for an EV charger). On an empty sheet, generates a board.
 */
export function appendGroupOps(
  project: Project,
  sheetId: Id,
  rcd: Record<string, PropValue>,
  circuits: CircuitSpec[],
  analysis: Analysis = analyse(project),
): Op[] {
  const onSheet = analysis.circuits.filter((c) => c.sheet.id === sheetId);
  const origin = onSheet.map((c) => c.rcds.at(-1)).find(Boolean);
  if (!origin)
    return generateBoardOps(project, sheetId, {
      mainRcd: { sensitivityMa: 300, rcdType: 'A', ratingA: 40 },
      groups: [{ rcd, circuits }],
    });
  const devices = Object.values(project.elements).filter(
    (e) => e.sheetId === sheetId && getSymbol(e.symbolId)?.category === 'protection',
  );
  // First-level devices hang from the main bus, 5 mm below it.
  const firstLevel = devices.filter(
    (e) =>
      onSheet.some((c) => c.device.id === e.id && c.rcds.length === 1 && c.rcds[0]!.id === origin.id) ||
      (e.symbolId === 'iec.rcd' &&
        e.id !== origin.id &&
        e.y < Math.min(...onSheet.map((c) => c.device.y)) + 1),
  );
  const busY = Math.min(...firstLevel.map((e) => e.y)) - 5;
  const rightFirst = Math.max(...firstLevel.map((e) => e.x + 5));
  const globalRight = Math.max(...devices.map((e) => e.x + 5));
  const b = new Builder(project, sheetId);
  const x = globalRight + COLUMN;
  b.wire(pt(rightFirst, busY), pt(x, busY));
  const r = b.element('iec.rcd', x - 5, busY + 5, b.nextDeviceTag(), rcd);
  b.wire(pt(x, busY), port(r, 'in'));
  const subBus = busY + 32.5;
  b.wire(port(r, 'out'), pt(x, subBus));
  circuits.forEach((c, i) => {
    const cx = x + i * COLUMN;
    placeCircuit(b, c, cx, subBus, pt(cx, subBus));
  });
  if (circuits.length > 1) b.wire(pt(x, subBus), pt(x + (circuits.length - 1) * COLUMN, subBus));
  return b.ops;
}

/**
 * Give every circuit a letter (creating circuit records where missing) and number its points
 * top-to-bottom: A1, A2… Elements on other sheets carrying an old point code (position plan)
 * are renamed too, so cross-references survive.
 */
export function renumberOps(project: Project, analysis: Analysis = analyse(project)): Op[] {
  const ops: Op[] = [];
  let working = project;
  const renamed = new Map<string, string>();
  const ordered = [...analysis.circuits].sort(
    (a, b) => a.sheet.order - b.sheet.order || a.device.y - b.device.y || a.device.x - b.device.x,
  );
  // Keep existing letters; assign new ones in drawing order.
  for (const c of ordered) {
    if (c.circuit) continue;
    if (!c.points.length) continue;
    const id = newId();
    const circuit: Circuit = {
      id,
      code: nextCircuitCode(working),
      label: '',
      cable: { type: c.cable.type, sectionMm2: c.cable.sectionMm2, cores: c.cable.cores || 3 },
      legacy: false,
    };
    const add: Op = { op: 'add', coll: 'circuits', value: circuit };
    const link: Op = { op: 'update', coll: 'elements', id: c.device.id, patch: { circuitId: id } };
    ops.push(add, link);
    working = applyTransaction(working, { label: '', ops: [add, link] }).project;
    c.code = circuit.code;
  }
  for (const c of ordered) {
    const code = c.code || working.circuits[c.device.circuitId ?? '']?.code;
    if (!code) continue;
    c.points.forEach((p, i) => {
      const tag = `${code}${i + 1}`;
      if (p.element.tag !== tag) {
        if (p.element.tag) renamed.set(p.element.tag, tag);
        ops.push({ op: 'update', coll: 'elements', id: p.element.id, patch: { tag } });
      }
    });
  }
  const singleLineIds = new Set(analysis.circuits.flatMap((c) => c.points.map((p) => p.element.id)));
  for (const el of Object.values(project.elements)) {
    if (singleLineIds.has(el.id)) continue;
    const sheet = project.sheets[el.sheetId];
    if (sheet?.kind !== 'position-plan') continue;
    const to = renamed.get(el.tag);
    if (to) ops.push({ op: 'update', coll: 'elements', id: el.id, patch: { tag: to } });
  }
  return ops;
}

// ---------- removing and moving circuits ----------

const near = (a: number, b: number) => Math.abs(a - b) < 0.01;

/**
 * What a circuit column is made of on its sheet: its device, points, circuit record, the wires
 * attached to them and the vertical circuit line, plus where it hangs from its bus.
 */
function circuitColumn(project: Project, deviceId: Id, analysis: Analysis) {
  const c = analysis.circuits.find((x) => x.device.id === deviceId);
  const dev = project.elements[deviceId];
  const def = dev && getSymbol(dev.symbolId);
  const inPort = def?.ports.find((p) => p.id === 'in');
  if (!c || !dev || !def || !inPort) return undefined;
  const x = portPosition(def, dev, inPort).x;
  const top = portPosition(def, dev, inPort).y;
  const elements = new Set<Id>([
    dev.id,
    ...c.points.filter((p) => p.element.sheetId === dev.sheetId).map((p) => p.element.id),
  ]);
  const wires = new Set<Id>();
  let busY: number | undefined;
  for (const w of Object.values(project.wires)) {
    if (w.sheetId !== dev.sheetId) continue;
    const ends = [w.from, w.to];
    const attached = ends.some((e) => e.kind === 'port' && elements.has(e.elementId));
    const line = ends.every((e) => e.kind === 'point' && near(e.x, x) && e.y > top) && !w.via.length;
    if (attached || line) wires.add(w.id);
    // The drop from the bus to the device's input: its free end is on the bus.
    if (ends.some((e) => e.kind === 'port' && e.elementId === dev.id && e.portId === inPort.id)) {
      const free = ends.find((e) => e.kind === 'point');
      if (free?.kind === 'point') busY = free.y;
    }
  }
  return { c, dev, x, busY, elements, wires };
}

/**
 * Points removed from a circuit on the diagram also leave the position plan (same tag, A1, A2…),
 * with their wires there, so the plan and the diagram still match (R-DOC-01).
 */
function planPointsOps(project: Project, removedTags: Set<string>, keptTags: Set<string> = new Set()): Op[] {
  const gone = new Set(
    Object.values(project.elements)
      .filter(
        (e) =>
          project.sheets[e.sheetId]?.kind === 'position-plan' &&
          removedTags.has(e.tag) &&
          !keptTags.has(e.tag),
      )
      .map((e) => e.id),
  );
  if (!gone.size) return [];
  const ops: Op[] = [];
  for (const w of Object.values(project.wires))
    if ([w.from, w.to].some((e) => e.kind === 'port' && gone.has(e.elementId)))
      ops.push({ op: 'remove', coll: 'wires', id: w.id });
  for (const id of gone) ops.push({ op: 'remove', coll: 'elements', id });
  return ops;
}

/**
 * Remove a circuit from the single-line diagram: its device, points, circuit record and wires.
 * The columns to its right move left to close the gap, and a bus ending at the column is
 * shortened to the last circuit still on it, so buses stay continuous. Buses are often one long
 * wire with the drops tapped on it, so they are shortened or moved, never cut. Points of the
 * circuit on other sheets (position plan) are kept.
 */
export function deleteCircuitOps(
  project: Project,
  deviceId: Id,
  analysis: Analysis = analyse(project),
  keepPlan = false,
): Op[] {
  const col = circuitColumn(project, deviceId, analysis);
  if (!col) return [];
  const { c, dev, x, busY, elements, wires } = col;
  const ops: Op[] = keepPlan
    ? []
    : planPointsOps(
        project,
        new Set([...elements].filter((id) => id !== deviceId).map((id) => project.elements[id]!.tag)),
      );
  for (const id of wires) ops.push({ op: 'remove', coll: 'wires', id });
  for (const id of elements) ops.push({ op: 'remove', coll: 'elements', id });
  if (c.circuit) ops.push({ op: 'remove', coll: 'circuits', id: c.circuit.id });
  if (busY === undefined) return ops;
  let after = applyTransaction(project, { label: '', ops }).project;
  const onSheet = Object.values(after.wires).filter((w) => w.sheetId === dev.sheetId);
  const isBus = (w: Wire) =>
    !w.via.length &&
    w.from.kind === 'point' &&
    w.to.kind === 'point' &&
    near(w.from.y, busY) &&
    near(w.to.y, busY);
  // Where something still hangs from this bus line: drops to devices, an RCD's output.
  const taps = onSheet
    .filter((w) => !isBus(w))
    .flatMap((w) => [w.from, w.to])
    .filter((e): e is Extract<WireEnd, { kind: 'point' }> => e.kind === 'point' && near(e.y, busY))
    .map((e) => e.x);
  for (const w of onSheet.filter(isBus)) {
    const a = w.from as Extract<WireEnd, { kind: 'point' }>;
    const b = w.to as Extract<WireEnd, { kind: 'point' }>;
    const atX = near(a.x, x) ? 'from' : near(b.x, x) ? 'to' : undefined;
    if (!atX) continue;
    const other = atX === 'from' ? b : a;
    if (other.x > x) continue; // runs to the right: the shift below shortens it
    const last = Math.max(-Infinity, ...taps.filter((t) => t < x - 0.01));
    if (last <= other.x + 0.01) ops.push({ op: 'remove', coll: 'wires', id: w.id });
    else
      ops.push({
        op: 'update',
        coll: 'wires',
        id: w.id,
        patch: { [atX]: { kind: 'point', x: last, y: busY } },
      });
  }
  after = applyTransaction(project, { label: '', ops }).project;
  // The column also holds what fed it (an RCD, the supply chain): close the gap only if other
  // circuits hang from the same bus further right, else the next group would land on that RCD.
  const occupied = Object.values(after.elements).some(
    (e) => e.sheetId === dev.sheetId && near(e.x + 5, x) && e.y < dev.y,
  );
  const sameBusRight = taps.some((t) => t > x + 0.01);
  const hasRight = Object.values(after.elements).some((e) => e.sheetId === dev.sheetId && e.x + 5 > x + 1);
  if (hasRight && (!occupied || sameBusRight)) {
    const shift = shiftRightOps(after, dev.sheetId, x + 1, -COLUMN);
    ops.push(...shift);
    after = applyTransaction(after, { label: '', ops: shift }).project;
    // A bus segment that started at the column and ended at the next one is now empty.
    for (const w of Object.values(after.wires))
      if (
        w.sheetId === dev.sheetId &&
        w.from.kind === 'point' &&
        w.to.kind === 'point' &&
        near(w.from.x, w.to.x) &&
        near(w.from.y, w.to.y)
      )
        ops.push({ op: 'remove', coll: 'wires', id: w.id });
  }
  return ops;
}

/** The circuit as a spec, to draw it again elsewhere (points top to bottom, props kept). */
export function specOf(project: Project, deviceId: Id, analysis: Analysis): CircuitSpec | undefined {
  const c = analysis.circuits.find((x) => x.device.id === deviceId);
  if (!c) return undefined;
  const sym = c.device.symbolId;
  if (sym !== 'iec.breaker' && sym !== 'iec.rcbo' && sym !== 'iec.fuse') return undefined;
  const points = [...c.points]
    .filter((p) => p.element.sheetId === c.device.sheetId)
    .sort((a, b) => a.element.y - b.element.y)
    .map((p) => ({ symbolId: p.element.symbolId, props: { ...p.element.props } }));
  return {
    label: c.circuit?.label ?? '',
    device: { symbolId: sym, props: { ...c.device.props } },
    points,
    ...(c.circuit?.cable.sectionMm2 ? { cable: { ...c.circuit.cable } } : {}),
    ...(c.circuit?.legacy ? { legacy: true } : {}),
  };
}

/**
 * Move a circuit under another RCD (or to the main bus when `rcdId` is undefined): drawn again
 * after that RCD's circuits, keeping its letter, device tag, points and cable.
 */
export function moveCircuitOps(
  project: Project,
  deviceId: Id,
  rcdId: Id | undefined,
  analysis: Analysis = analyse(project),
): Op[] {
  const c = analysis.circuits.find((x) => x.device.id === deviceId);
  const spec = specOf(project, deviceId, analysis);
  if (!c || !spec || (rcdId && !project.elements[rcdId])) return [];
  const removed = deleteCircuitOps(project, deviceId, analysis, true);
  const p1 = applyTransaction(project, { label: '', ops: removed }).project;
  const added = appendCircuitOps(p1, c.device.sheetId, spec, rcdId);
  // Keep the circuit's letter and tags: the plan and the multi-line diagram refer to them.
  const newCode = added.find((o) => o.op === 'add' && o.coll === 'circuits');
  const fromCode = newCode?.op === 'add' && newCode.coll === 'circuits' ? newCode.value.code : undefined;
  const code = c.circuit?.code ?? c.code;
  const kept = added.map((o): Op => {
    if (o.op !== 'add') return o;
    if (o.coll === 'circuits' && code) return { ...o, value: { ...o.value, code } };
    if (o.coll === 'elements' && o.value.id !== undefined) {
      if (o.value.symbolId === c.device.symbolId && o.value.circuitId)
        return { ...o, value: { ...o.value, tag: c.device.tag } };
      if (fromCode && code && o.value.tag.startsWith(fromCode))
        return { ...o, value: { ...o.value, tag: code + o.value.tag.slice(fromCode.length) } };
    }
    return o;
  });
  return [...removed, ...kept];
}

/** Replace a circuit's points on the single-line diagram (drawn again from its device down). */
export function setPointsOps(
  project: Project,
  deviceId: Id,
  points: PointSpec[],
  analysis: Analysis = analyse(project),
  /** Existing elements the points are (same order): they keep their id, so a kept point shows as moved, not new. */
  reuse: Array<Id | undefined> = [],
): Op[] {
  const col = circuitColumn(project, deviceId, analysis);
  if (!col) return [];
  const ops: Op[] = [];
  for (const id of col.wires) {
    const w = project.wires[id]!;
    // Keep the drop from the bus: only what hangs below the device goes.
    const drop = [w.from, w.to].some(
      (e) => e.kind === 'port' && e.elementId === deviceId && e.portId === 'in',
    );
    if (!drop) ops.push({ op: 'remove', coll: 'wires', id });
  }
  for (const id of col.elements) if (id !== deviceId) ops.push({ op: 'remove', coll: 'elements', id });
  // The cable label sits on the wire leaving the device: carry it to the new circuit line.
  const label = [...col.wires]
    .map((id) => project.wires[id]!)
    .find((w) =>
      [w.from, w.to].some((e) => e.kind === 'port' && e.elementId === deviceId && e.portId === 'out'),
    )?.label;
  const p1 = applyTransaction(project, { label: '', ops }).project;
  const appended = appendPointsOps(p1, deviceId, points);
  const newTags = new Set(
    appended.flatMap((o) => (o.op === 'add' && o.coll === 'elements' ? [o.value.tag] : [])),
  );
  const oldTags = new Set(
    [...col.elements].filter((id) => id !== deviceId).map((id) => project.elements[id]!.tag),
  );
  ops.push(...planPointsOps(project, oldTags, newTags));
  const newIds = appended.flatMap((o) =>
    o.op === 'add' && o.coll === 'elements' && o.value.id !== deviceId ? [o.value.id] : [],
  );
  const same = new Map<Id, Id>();
  newIds.forEach((id, i) => {
    const old = reuse[i];
    if (old && col.elements.has(old)) same.set(id, old);
  });
  const end = (e: WireEnd): WireEnd =>
    e.kind === 'port' && same.has(e.elementId) ? { ...e, elementId: same.get(e.elementId)! } : e;
  const added = appended.map((o): Op => {
    if (o.op !== 'add') return o;
    if (o.coll === 'elements' && same.has(o.value.id))
      return { ...o, value: { ...o.value, id: same.get(o.value.id)! } };
    if (o.coll !== 'wires') return o;
    const w = { ...o.value, from: end(o.value.from), to: end(o.value.to) };
    const fromDevice = w.from.kind === 'port' && w.from.elementId === deviceId;
    return { ...o, value: label && fromDevice ? { ...w, label } : w };
  });
  return [...ops, ...added];
}
